import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/lib/components/Dialog";
import { Button } from "@/lib/components/Button/Button";
import { useEffect, useState } from "react";
import ReferralInviteView from "./ReferralInviteView";
import ReferralEarnedCreditsView from "./ReferralEarnedCreditsView";
import { useParams } from "react-router";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { IconArrowLeft, IconX } from "@tabler/icons-react";
import { cn } from "@/lib/utils";

type Props = {
  source: string;
  children?: React.ReactNode;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
};

export default function ReferralOverviewDialog({
  source,
  children,
  isOpen,
  setIsOpen,
}: Props) {
  const [view, setView] = useState<"invite" | "earnedCredits">("invite");

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setView("invite");
    }
    setIsOpen?.(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent
        className="max-h-full w-130 max-w-full overflow-hidden border-0"
        classNameInner="p-0 sm:p-0"
        showCloseButton={false}
      >
        <DialogTitle hidden>Earn $100</DialogTitle>
        <DialogDescription hidden>
          Invite friends or share your video to earn free credits
        </DialogDescription>
        <ReferralDialogOpenedTracker source={source} />
        <div
          className={cn(
            "flex min-h-0 flex-col",
            view === "earnedCredits" && "invisible",
          )}
        >
          <TeamQuote />
          <ReferralInviteView
            onShowEarnedCredits={() => setView("earnedCredits")}
          />
        </div>
        {view === "earnedCredits" && (
          <div className="absolute inset-0 flex min-h-0 flex-col">
            <div className="flex items-center gap-2 border-b border-border py-3 pr-12 pl-2 md:pl-4">
              <Button
                variant="ghost"
                size="iconSm"
                onClick={() => setView("invite")}
              >
                <IconArrowLeft />
                <span className="sr-only">Back</span>
              </Button>
              <h2 className="font-semibold">Earned credits</h2>
            </div>
            <ReferralEarnedCreditsView />
          </div>
        )}
        <DialogClose asChild>
          <Button
            variant="ghost"
            size="iconSm"
            className={cn(
              "absolute top-2 right-2",
              view === "invite" &&
                "text-white hover:bg-white/20 hover:text-white",
            )}
          >
            <IconX />
            <span className="sr-only">Close</span>
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}

function TeamQuote() {
  return (
    <div className="relative">
      <img
        src="/referral-team.jpg"
        alt="The Amplifiles team"
        className="aspect-800/426 w-full object-cover"
      />
      <div className="absolute inset-0 flex flex-col justify-end bg-linear-to-t from-black/90 via-black/40 to-black/30 px-8 pb-6">
        <div className="relative">
          <span
            aria-hidden
            className="pointer-events-none absolute -top-7 -left-4 font-serif text-[80px] leading-none text-white/20 select-none"
          >
            &ldquo;
          </span>
          <p className="relative text-sm leading-relaxed text-white italic">
            We&rsquo;re a small team developing this product with passion. Thank
            you for every referral. It helps us make Amplifiles even better.
          </p>
          <img
            src="/amplifiles-signature.png"
            alt="The Amplifiles team"
            className="relative mt-3 h-6.5 w-auto"
          />
        </div>
      </div>
    </div>
  );
}

function ReferralDialogOpenedTracker({ source }: { source: string }) {
  const { teamExternalId } = useParams();

  useEffect(() => {
    AnalyticsApi.logReferralDialogOpened(source, teamExternalId);
  }, [source, teamExternalId]);

  return null;
}
